import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Loader2, Eye, EyeOff, Copy, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ResetPasswordButton — admin-side manual password reset for any user.
 *
 * Renders a small red "key" icon button. Clicking it opens a modal where
 * the admin either types a new password OR clicks "Generate" to auto-fill
 * a 12-char random one. On submit we hit
 * POST /api/admin/users/:user_id/set-password with the new value +
 * a reason string (required for audit clarity).
 *
 * Props:
 *   user      — { id, email, name }
 *   getAuthHeader() → { Authorization: '...' }
 *
 * Best-effort UX niceties:
 *   • Password strength gate (min 8 chars — same as backend Pydantic)
 *   • Optional show/hide toggle
 *   • Copy-to-clipboard so the admin can DM/text the credential securely
 */
export default function ResetPasswordButton({ user, getAuthHeader }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const openModal = () => {
    setPassword('');
    setReason('');
    setShowPw(false);
    setOpen(true);
  };

  const generate = () => {
    // 12-char password with mixed upper/lower/digits/symbols.
    const alpha = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    const symbols = '!@#$%&*';
    let out = '';
    const cryptoObj = window.crypto || window.msCrypto;
    const buf = new Uint32Array(10);
    cryptoObj.getRandomValues(buf);
    for (let i = 0; i < 10; i++) out += alpha[buf[i] % alpha.length];
    cryptoObj.getRandomValues(buf);
    out += symbols[buf[0] % symbols.length];
    out += (buf[1] % 90) + 10; // 2-digit number
    setPassword(out);
    setShowPw(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Password copied to clipboard');
    } catch {
      toast.error('Copy failed — please select and copy manually');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${user.id}/set-password`,
        { new_password: password, reason: reason.trim() },
        { headers: getAuthHeader() },
      );
      toast.success(`Password reset for ${user.email}`);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={openModal}
        className="bg-indigo-600 hover:bg-indigo-700 text-xs"
        title={`Manually reset password for ${user.email}`}
        data-testid={`reset-password-btn-${user.id}`}
      >
        <KeyRound className="w-3 h-3" />
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !submitting && setOpen(false)}
          data-testid="reset-password-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center">
                <KeyRound size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">Reset password</h3>
                <p className="text-sm text-slate-400">
                  for <span className="text-slate-200 font-medium">{user.email}</span>
                </p>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  New password
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="min 8 chars"
                      className="w-full px-3 py-2 pr-9 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      minLength={8}
                      maxLength={128}
                      required
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <Button
                    type="button"
                    onClick={generate}
                    className="bg-slate-700 hover:bg-slate-600 text-xs"
                    data-testid="reset-password-generate"
                  >
                    Generate
                  </Button>
                  {password && (
                    <Button
                      type="button"
                      onClick={copy}
                      className="bg-slate-700 hover:bg-slate-600"
                      title="Copy to clipboard"
                      data-testid="reset-password-copy"
                    >
                      <Copy size={14} />
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Reason (optional, audit-logged)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. member locked out, resend email undelivered"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  maxLength={500}
                  data-testid="reset-password-reason"
                />
              </div>

              <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <ShieldAlert className="text-amber-400 flex-shrink-0 mt-0.5" size={14} />
                <p className="text-xs text-amber-200">
                  The user won&apos;t receive an email. Communicate the new password securely
                  and ask them to change it after logging in.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  className="bg-slate-700 hover:bg-slate-600 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || password.length < 8}
                  className="bg-indigo-600 hover:bg-indigo-700 text-xs disabled:opacity-50"
                  data-testid="reset-password-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving…
                    </>
                  ) : (
                    'Set new password'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
